/**
 * Per-user page exclusions.
 *
 * An admin can exclude specific pages for a specific user (in User Management)
 * even when the user's role would normally grant access. Exclusions are stored
 * on the user doc as an array of normalized route paths (e.g. ['/users','/pos'])
 * and enforced in three places that share these helpers:
 *   - the Sidebar (hide the link)
 *   - the SimplePageList nav (hide the link)
 *   - a central route guard in Layout (block deep-links / direct navigation)
 */

/** Normalize a path/route to a comparable form: leading slash, no trailing slash, lowercase. */
export const normalizePath = (input?: string | null): string => {
  if (!input) return '';
  let s = String(input).trim();
  if (!s) return '';
  // Drop query string / hash if a full path slipped in.
  s = s.split('?')[0].split('#')[0];
  if (!s.startsWith('/')) s = '/' + s;
  if (s.length > 1) s = s.replace(/\/+$/, '');
  return s.toLowerCase();
};

/**
 * Is `pathname` covered by one of the user's excluded paths?
 * Matches the exact path or any nested route beneath it (e.g. excluding
 * `/chats` also blocks `/chats/abc`). The root path `/` only matches exactly.
 */
export const isPathExcluded = (pathname: string, excluded?: string[] | null): boolean => {
  if (!excluded || excluded.length === 0) return false;
  const target = normalizePath(pathname);
  if (!target) return false;
  return excluded.some((raw) => {
    const ex = normalizePath(raw);
    if (!ex) return false;
    if (ex === '/') return target === '/';
    return target === ex || target.startsWith(ex + '/');
  });
};
